import { ImageIcon } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useRef, useState } from "react"
import type { FileBrowserManager } from "../store/managers/FileBrowserManager"
import { ImageLightbox } from "./ui/ImageLightbox"
import { getFileName } from "./utils/paths"

const INLINE_IMAGE_AUTO_PREVIEW_BYTES = 25 * 1024 * 1024
const INLINE_IMAGE_LOAD_ROOT_MARGIN = "640px 320px"

type ImageLoadState = { status: "idle" | "loading" } | { status: "loaded"; src: string } | { status: "error"; message: string }

export const MarkdownLocalImage = observer(function MarkdownLocalImage({
    path,
    alt,
    title,
    fileBrowser,
    onOpenPath,
}: {
    path: string
    alt: string
    title: string | null
    fileBrowser: FileBrowserManager | null
    onOpenPath: ((path: string) => void) | null
}) {
    const containerRef = useRef<HTMLElement>(null)
    const [nearViewport, setNearViewport] = useState(() => typeof IntersectionObserver === "undefined")
    const [loadState, setLoadState] = useState<ImageLoadState>({ status: "idle" })
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const label = alt.trim() || getFileName(path)
    const workingDir = fileBrowser?.workingDir ?? ""
    const shouldLoad = nearViewport || lightboxOpen

    useEffect(() => {
        const target = containerRef.current
        if (!target || typeof IntersectionObserver === "undefined") {
            setNearViewport(true)
            return
        }
        const observer = new IntersectionObserver(([entry]) => setNearViewport(entry?.isIntersecting === true), {
            rootMargin: INLINE_IMAGE_LOAD_ROOT_MARGIN,
        })
        observer.observe(target)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!shouldLoad || !fileBrowser) {
            setLoadState({ status: "idle" })
            return
        }

        const controller = new AbortController()
        let objectURL: string | null = null
        setLoadState({ status: "loading" })

        void fileBrowser
            .describeWorkspaceFileReference(path)
            .then(async (description) => {
                if (!description) throw new Error("Image is unavailable or outside the task working directory")
                if (description.previewKind !== "image") throw new Error("The referenced file is not an image")
                if (description.size > INLINE_IMAGE_AUTO_PREVIEW_BYTES) {
                    throw new Error("Image is too large to preview inline")
                }

                const bytes = await fileBrowser.readWorkspaceFileReferenceBytes(path, { signal: controller.signal })
                if (!bytes.mediaType.startsWith("image/")) throw new Error("The referenced file is not an image")
                return bytes.blob
            })
            .then((blob) => {
                if (controller.signal.aborted) return
                objectURL = URL.createObjectURL(blob)
                setLoadState({ status: "loaded", src: objectURL })
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) return
                setLoadState({ status: "error", message: cause instanceof Error ? cause.message : "Image preview failed" })
            })

        return () => {
            controller.abort()
            if (objectURL) URL.revokeObjectURL(objectURL)
        }
    }, [fileBrowser, path, shouldLoad, workingDir])

    const openPath = () => onOpenPath?.(path)

    return (
        <figure ref={containerRef} className="my-2 min-w-0 max-w-full">
            {loadState.status === "loaded" ? (
                <button
                    type="button"
                    className="btn block max-w-full cursor-zoom-in border border-border bg-base-200 p-0 hover:border-primary/50"
                    aria-label={`Open ${label}`}
                    onClick={() => setLightboxOpen(true)}
                >
                    <img
                        src={loadState.src}
                        alt={alt}
                        {...(title ? { title } : {})}
                        className="block max-h-[36rem] max-w-full object-contain"
                        loading="lazy"
                        decoding="async"
                    />
                </button>
            ) : (
                <button
                    type="button"
                    className="btn flex w-full items-center gap-2 border border-border bg-base-200 px-3 py-2 text-left text-sm hover:bg-base-300"
                    disabled={!onOpenPath}
                    onClick={openPath}
                >
                    <ImageIcon size={16} className="shrink-0 text-muted" />
                    <span className="min-w-0">
                        <span className="block truncate text-base-content">{label}</span>
                        <span className="block truncate text-xs text-muted">
                            {loadState.status === "error" ? loadState.message : loadState.status === "loading" ? "Loading image…" : path}
                        </span>
                    </span>
                </button>
            )}
            {title && loadState.status === "loaded" ? <figcaption className="mt-1 text-xs text-muted">{title}</figcaption> : null}
            {lightboxOpen && loadState.status === "loaded" ? <ImageLightbox src={loadState.src} alt={alt} onClose={() => setLightboxOpen(false)} /> : null}
        </figure>
    )
})
